import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createRecurringExpense, updateRecurringExpense } from '@coparent/shared/src/services/expenseService';
import { EXPENSE_CATEGORIES, RECURRING_FREQUENCIES, DAYS_OF_WEEK_SHORT } from '@coparent/shared/src/utils/constants';
import ChildMultiSelect from '../common/ChildMultiSelect';
import { HiX } from 'react-icons/hi';
import toast from 'react-hot-toast';

const RecurringExpenseForm = ({ onClose, recurringExpense }) => {
  const { currentUser } = useAuth();
  const { familyId, familyChildren, childrenMap, family } = useFamily();
  const [loading, setLoading] = useState(false);
  const [selectedChildIds, setSelectedChildIds] = useState(recurringExpense?.childIds || []);
  const isEditing = !!recurringExpense;

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: isEditing ? {
      description: recurringExpense.description || '',
      amount: recurringExpense.amount || '',
      category: recurringExpense.category || 'other',
      frequency: recurringExpense.frequency || 'monthly',
      dayOfMonth: recurringExpense.dayOfMonth || 1,
      dayOfWeek: recurringExpense.dayOfWeek ?? 0,
      biweeklyStartDate: recurringExpense.biweeklyStartDate || '',
      paidBy: recurringExpense.paidBy || currentUser.uid,
      splitRatio: recurringExpense.splitRatio ?? (family?.settings?.defaultSplitRatio || 50),
      note: recurringExpense.note || '',
    } : {
      frequency: 'monthly',
      dayOfMonth: 1,
      dayOfWeek: 0,
      category: 'other',
      paidBy: currentUser.uid,
      splitRatio: family?.settings?.defaultSplitRatio || 50,
    }
  });

  const frequency = watch('frequency');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const record = {
        description: data.description,
        amount: parseFloat(data.amount),
        category: data.category,
        frequency: data.frequency,
        childIds: selectedChildIds,
        paidBy: data.paidBy || currentUser.uid,
        splitRatio: parseInt(data.splitRatio),
        note: data.note || null,
        createdBy: currentUser.uid,
      };

      if (data.frequency === 'monthly') {
        record.dayOfMonth = parseInt(data.dayOfMonth);
      } else if (data.frequency === 'weekly') {
        record.dayOfWeek = parseInt(data.dayOfWeek);
      } else if (data.frequency === 'biweekly') {
        record.dayOfWeek = parseInt(data.dayOfWeek);
        record.biweeklyStartDate = data.biweeklyStartDate;
      }

      if (isEditing) {
        await updateRecurringExpense(familyId, recurringExpense.id, record);
        toast.success('Recurring expense updated!');
      } else {
        await createRecurringExpense(familyId, record);
        toast.success('Recurring expense created!');
      }
      onClose();
    } catch (error) {
      toast.error(isEditing ? 'Failed to update' : 'Failed to create');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">
            {isEditing ? 'Edit Recurring Expense' : 'Add Recurring Expense'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Description</label>
            <input
              {...register('description', { required: 'Required' })}
              type="text"
              className="input-base"
              placeholder="e.g., Cell phone plan"
            />
            {errors.description && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.description.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Amount</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300 font-semibold">$</span>
                <input
                  {...register('amount', { required: 'Required', min: { value: 0.01, message: 'Must be > 0' } })}
                  type="number"
                  step="0.01"
                  className="input-base pl-8"
                  placeholder="0.00"
                />
              </div>
              {errors.amount && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.amount.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Frequency</label>
              <select {...register('frequency')} className="input-base">
                {RECURRING_FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          </div>

          {frequency === 'monthly' && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Day of Month</label>
              <select {...register('dayOfMonth')} className="input-base">
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-400">Limited to 1-28 to work for all months.</p>
            </div>
          )}

          {(frequency === 'weekly' || frequency === 'biweekly') && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Day of Week</label>
              <div className="flex gap-1.5">
                {DAYS_OF_WEEK_SHORT.map((day, i) => {
                  const selectedDay = parseInt(watch('dayOfWeek'));
                  return (
                    <label
                      key={i}
                      className={`flex-1 text-center py-2 rounded-lg cursor-pointer text-xs font-semibold transition-colors border ${
                        selectedDay === i
                          ? 'bg-canopy-600 text-white border-canopy-600'
                          : 'bg-surface-50 text-ink-500 border-surface-200 hover:border-canopy-300'
                      }`}
                    >
                      <input
                        type="radio"
                        {...register('dayOfWeek')}
                        value={i}
                        className="sr-only"
                      />
                      {day}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {frequency === 'biweekly' && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">First Occurrence Date</label>
              <input
                {...register('biweeklyStartDate', { required: frequency === 'biweekly' ? 'Required for biweekly' : false })}
                type="date"
                className="input-base"
              />
              <p className="mt-1 text-xs text-ink-400">Pick a date in the first week this expense occurs.</p>
              {errors.biweeklyStartDate && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.biweeklyStartDate.message}</p>}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Category</label>
            <select {...register('category')} className="input-base">
              {EXPENSE_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {familyChildren.length > 0 && (
            <ChildMultiSelect
              familyChildren={familyChildren}
              childrenMap={childrenMap}
              selectedChildIds={selectedChildIds}
              onChange={setSelectedChildIds}
            />
          )}

          {family && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Paid by</label>
              <select {...register('paidBy')} className="input-base">
                {family.parentIds.map(pid => (
                  <option key={pid} value={pid}>
                    {family.parentProfiles[pid]?.displayName}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">
              Split: other parent pays
            </label>
            <div className="flex items-center gap-3">
              <input
                {...register('splitRatio')}
                type="range"
                min="0"
                max="100"
                className="flex-1 accent-ink-800"
              />
              <span className="text-sm font-bold text-ink-700 w-12 text-right">
                {watch('splitRatio')}%
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Note (optional)</label>
            <input
              {...register('note')}
              type="text"
              className="input-base"
              placeholder="Additional details..."
            />
          </div>

          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecurringExpenseForm;
